import { useMemo, useState } from "react";
import { CalendarDays, Eye, EyeOff, Landmark, Pencil, PiggyBank, Plus, Receipt, ShoppingBasket, Trash2, WalletCards } from "lucide-react";
import { AssetEditor, FinancePlanEditor } from "../components/finance/FinanceEditors";
import { Button, EmptyState, PageHeader } from "../components/ui";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { formatDate, formatMoney } from "../lib/format";
import type { Asset, ExpenseRecord, FinancePlan } from "../types";

const assetTypeLabels: Record<Asset["asset_type"], string> = {
  CASH: "现金",
  BANK: "银行存款",
  INVESTMENT: "投资",
  RECEIVABLE: "应收款",
  FIXED: "固定资产",
  OTHER: "其他"
};

const cadenceLabels: Record<FinancePlan["cadence"], string> = {
  ONE_TIME: "一次性计划",
  MONTHLY: "每月",
  YEARLY: "每年"
};

const planStatusLabels: Record<FinancePlan["status"], string> = {
  ACTIVE: "进行中",
  PAUSED: "已暂停",
  COMPLETED: "已完成"
};

export function FinancePage() {
  const { data, mutate } = useApp();
  const [assetEditor, setAssetEditor] = useState<Asset | "new" | null>(null);
  const [planEditor, setPlanEditor] = useState<FinancePlan | "new" | null>(null);
  const [hideMoney, setHideMoney] = useState(() => localStorage.getItem("eo-hide-money") === "1");

  const toggleHideMoney = () => {
    setHideMoney((prev) => {
      const next = !prev;
      localStorage.setItem("eo-hide-money", next ? "1" : "0");
      return next;
    });
  };

  const money = (minor: number | string, currency: string) =>
    hideMoney ? `${currency === "CNY" ? "¥" : currency} ••••` : formatMoney(Number(minor), currency);

  // 消费记录：按日期分组 + 本月合计（hooks 必须在条件 return 前）
  const expenseDays = useMemo(() => {
    const map = new Map<string, ExpenseRecord[]>();
    for (const item of data?.expenses ?? []) {
      const list = map.get(item.record_date) ?? [];
      list.push(item);
      map.set(item.record_date, list);
    }
    return [...map.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, 14)
      .map(([date, items]) => ({
        date,
        total: items.reduce((sum, i) => sum + i.amount_minor, 0),
        items
      }));
  }, [data?.expenses]);
  const monthPrefix = new Date().toISOString().slice(0, 7);
  const monthlyTotal = (data?.expenses ?? [])
    .filter((e) => e.record_date.startsWith(monthPrefix))
    .reduce((sum, e) => sum + e.amount_minor, 0);

  // 本月分类占比（环形图数据）
  const expenseChart = useMemo(() => {
    const monthItems = (data?.expenses ?? []).filter((e) => e.record_date.startsWith(monthPrefix));
    const byCat = new Map<string, number>();
    for (const item of monthItems) {
      byCat.set(item.category, (byCat.get(item.category) ?? 0) + item.amount_minor);
    }
    const palette = [
      "var(--treasury-chart-1)",
      "var(--treasury-chart-2)",
      "var(--treasury-chart-3)",
      "var(--treasury-chart-4)",
      "var(--treasury-chart-5)",
      "var(--treasury-chart-6)"
    ];
    const entries = [...byCat.entries()].sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((sum, [, v]) => sum + v, 0) || 1;
    let cursor = 0;
    const segments = entries.map(([name, value], index) => {
      const from = (cursor / total) * 100;
      cursor += value;
      const to = (cursor / total) * 100;
      return { name, color: palette[index % palette.length], from, to };
    });
    const legend = entries.map(([name, value], index) => ({
      name,
      amount: value,
      percent: Math.round((value / total) * 100),
      color: palette[index % palette.length]
    }));
    return { segments, legend };
  }, [data?.expenses, monthPrefix]);

  // 本月每日消费趋势（折线图）
  const expenseTrend = useMemo(() => {
    const monthItems = (data?.expenses ?? []).filter((e) => e.record_date.startsWith(monthPrefix));
    const byDay = new Map<string, number>();
    for (const item of monthItems) {
      byDay.set(item.record_date, (byDay.get(item.record_date) ?? 0) + item.amount_minor);
    }
    const days = Array.from({ length: new Date().getDate() }, (_, i) => {
      const day = String(i + 1).padStart(2, "0");
      return byDay.get(`${monthPrefix}-${day}`) ?? 0;
    });
    const peak = Math.max(...days, 1);
    const points = days
      .map((value, index) => `${(index / Math.max(1, days.length - 1)) * 100},${40 - (value / peak) * 34 - 3}`)
      .join(" ");
    return { points, days, peak };
  }, [data?.expenses, monthPrefix]);

  // 分类树展开状态
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const toggleCat = (name: string) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const categoryDetails = useMemo(() => {
    const monthItems = (data?.expenses ?? []).filter((e) => e.record_date.startsWith(monthPrefix));
    const map = new Map<string, typeof monthItems>();
    for (const item of monthItems) {
      const list = map.get(item.category) ?? [];
      list.push(item);
      map.set(item.category, list);
    }
    return map;
  }, [data?.expenses, monthPrefix]);

  if (!data) return null;

  const activePlans = data.financialPlans.filter((plan) => plan.status === "ACTIVE");
  const latestSnapshot = data.assets.reduce(
    (latest, asset) => (asset.as_of_date > latest ? asset.as_of_date : latest),
    ""
  );

  return (
    <div className="page page--finance finance-page finance-treasury">
      <PageHeader
        title="财务与资产"
        description="记录当下，安排下一步。流水只作参考，不改写你的资产盘点。"
        actions={
          <div className="button-group">
            <Button variant="ghost" size="sm" onClick={toggleHideMoney} icon={hideMoney ? <EyeOff size={16} /> : <Eye size={16} />} title="隐藏/显示金额">
              {hideMoney ? "显示金额" : "隐藏金额"}
            </Button>
            <Button variant="secondary" onClick={() => setAssetEditor(data.assets[0] ?? "new")} icon={<WalletCards size={16} />}>更新财务现状</Button>
            <Button onClick={() => setPlanEditor("new")} icon={<Plus size={16} />}>新增计划</Button>
          </div>
        }
      />

      <section className="treasury-overview" aria-labelledby="treasury-overview-title">
        <header className="treasury-overview__mast">
          <div>
            <Landmark size={18} aria-hidden="true" />
            <h2 id="treasury-overview-title">资产总览</h2>
          </div>
          <span>{latestSnapshot ? `盘点于 ${formatDate(latestSnapshot, { year: "numeric", month: "long", day: "numeric" })}` : "等待首次盘点"}</span>
        </header>
        <div className="treasury-overview__body">
          <div className="treasury-balance">
            <span>当前净额 · 按币种分别计算</span>
            {data.assetTotals.length ? (
              <div className="treasury-balance__values">
                {data.assetTotals.map((total) => (
                  <strong key={total.currency}>{money(total.total_minor, total.currency)}</strong>
                ))}
              </div>
            ) : (
              <strong className="treasury-balance__empty">尚未登记</strong>
            )}
            <p>不同币种不按未知汇率合并，负债已计入对应币种净额。</p>
          </div>
          <dl className="treasury-overview__facts">
            <div><dt>资产条目</dt><dd>{data.assets.length}</dd><span>项真实盘点</span></div>
            <div><dt>财务计划</dt><dd>{activePlans.length}</dd><span>项正在执行</span></div>
            <div><dt>消费记录</dt><dd>{data.expenses.length}</dd><span>笔已同步</span></div>
          </dl>
        </div>
      </section>

      <div className="treasury-studio">
        <section className="treasury-ledger" aria-labelledby="treasury-ledger-title">
          <header className="treasury-section-head">
            <div><h2 id="treasury-ledger-title">当前资产账簿</h2><p>余额变化后直接修改，并更新盘点日期。</p></div>
            <Button size="sm" variant="secondary" onClick={() => setAssetEditor("new")} icon={<Plus size={14} />}>新增一项</Button>
          </header>
          {data.assets.length ? (
            <div className="treasury-asset-list">
              {data.assets.map((asset, index) => (
                <article className="treasury-asset-row" key={asset.id}>
                  <span className="treasury-asset-row__index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                  <div className="treasury-asset-row__name">
                    <strong>{asset.name}</strong>
                    <span>{assetTypeLabels[asset.asset_type]} · 盘点于 {formatDate(asset.as_of_date)}</span>
                  </div>
                  <div className="treasury-asset-row__amount">
                    <strong className={asset.amount_minor < 0 ? "finance-amount--neg" : undefined}>{money(asset.amount_minor, asset.currency)}</strong>
                    <span>{asset.note || "暂无备注"}</span>
                  </div>
                  <div className="finance-row-actions">
                    <button type="button" onClick={() => setAssetEditor(asset)} aria-label={`编辑${asset.name}`}><Pencil size={15} /></button>
                    <button type="button" onClick={() => void mutate(() => api.remove("assets", asset.id), "")} aria-label={`删除${asset.name}`}><Trash2 size={15} /></button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState icon={<WalletCards size={22} />} title="还没有当前财务情况" description="例如“保险库资金 3000 元”，直接作为当前金额登记。" actionLabel="登记第一项" onAction={() => setAssetEditor("new")} compact />
          )}
        </section>

        <aside className="treasury-plans" aria-labelledby="treasury-plans-title">
          <header className="treasury-section-head treasury-section-head--dark">
            <div><h2 id="treasury-plans-title">计划轨道</h2><p>目标与开销上限，不代表金额已经发生。</p></div>
            <Button size="sm" onClick={() => setPlanEditor("new")} icon={<Plus size={14} />}>新增计划</Button>
          </header>
          {data.financialPlans.length ? (
            <div className="treasury-plan-list">
              {data.financialPlans.map((plan) => (
                <article className={`treasury-plan-row treasury-plan-row--${plan.plan_type.toLowerCase()}`} key={plan.id}>
                  <div className="treasury-plan-row__topline">
                    <span className="treasury-plan-row__type" aria-hidden="true">
                    {plan.plan_type === "SAVING" ? <PiggyBank size={18} /> : <ShoppingBasket size={18} />}
                    </span>
                    <span className={`finance-plan-status finance-plan-status--${plan.status.toLowerCase()}`}>{planStatusLabels[plan.status]}</span>
                    <div className="finance-row-actions">
                      <button type="button" onClick={() => setPlanEditor(plan)} aria-label={`编辑${plan.title}`}><Pencil size={15} /></button>
                      <button type="button" onClick={() => void mutate(() => api.remove("financial_plans", plan.id), "")} aria-label={`删除${plan.title}`}><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <div className="treasury-plan-row__body">
                    <strong>{plan.title}</strong>
                    <b>{money(plan.amount_minor, plan.currency)}</b>
                    <span>{plan.plan_type === "SAVING" ? "存钱目标" : "开销上限"}</span>
                    <span><CalendarDays size={13} /> {cadenceLabels[plan.cadence]}{plan.target_date ? ` · ${formatDate(plan.target_date)}前` : ""}</span>
                    {plan.note && <p>{plan.note}</p>}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState icon={<PiggyBank size={22} />} title="还没有财务计划" description="先写下近期最重要的一项存钱目标或开销安排。" actionLabel="新增第一项计划" onAction={() => setPlanEditor("new")} compact />
          )}
        </aside>
      </div>

      <section className="treasury-expenses" aria-labelledby="treasury-expenses-title">
        <header className="treasury-section-head treasury-section-head--expenses">
          <div><h2 id="treasury-expenses-title">消费观察</h2><p>来自钱迹的同步记录，只用于回看消费，不影响资产盘点。</p></div>
          <span className="treasury-sync-note">每日 22:00 更新</span>
        </header>
        {data.expenses.length ? (
          <div className="expense-month-summary">
            <div><strong>{formatMoney(monthlyTotal, "CNY")}</strong><span>本月消费</span></div>
            <div><strong>{data.expenses.length}</strong><span>已同步笔数</span></div>
            <div><strong>{formatMoney(monthlyTotal / Math.max(1, new Date().getDate()), "CNY")}</strong><span>日均</span></div>
          </div>
        ) : (
          <EmptyState icon={<Receipt size={22} />} title="还没有消费记录" description="今晚 22:00 自动从钱迹同步当天的消费明细。" compact />
        )}
        {monthlyTotal > 0 && (
          <>
            <div className="treasury-expense-insight">
              <div className="expense-chart">
                <div
                  className="expense-chart__ring"
                  style={{ background: `conic-gradient(${expenseChart.segments.map((s) => `${s.color} ${s.from}% ${s.to}%`).join(", ")})` }}
                >
                  <div className="expense-chart__hole">
                    <strong>{formatMoney(monthlyTotal, "CNY")}</strong>
                    <span>本月消费</span>
                  </div>
                </div>
                <div className="expense-chart__legend">
                  {expenseChart.legend.map((item) => (
                    <div className="expense-chart__legend-row" key={item.name}>
                      <i style={{ background: item.color }} />
                      <span>{item.name}</span>
                      <b>{formatMoney(item.amount, "CNY")}</b>
                      <em>{item.percent}%</em>
                    </div>
                  ))}
                </div>
              </div>
              <div className="expense-trend">
                <div className="expense-trend__head">
                  <span>本月消费趋势</span>
                  <b>峰值 {formatMoney(expenseTrend.peak, "CNY")} / 天</b>
                </div>
                <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="expense-trend__svg">
                  <polyline
                    points={expenseTrend.points}
                    fill="none"
                    stroke="var(--treasury-accent)"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
                <div className="expense-trend__axis">
                  <span>1日</span><span>{new Date().getDate()}日</span>
                </div>
              </div>
            </div>

            <div className="treasury-expense-ledgers">
              <div className="expense-cat-tree">
                <div className="expense-cat-tree__head">分类明细</div>
                {expenseChart.legend.map((item) => (
                  <div className="expense-cat-node" key={item.name}>
                    <button type="button" className="expense-cat-node__row" onClick={() => toggleCat(item.name)} aria-expanded={expandedCats.has(item.name)}>
                      <i style={{ background: item.color }} />
                      <span>{item.name}</span>
                      <em>{item.percent}%</em>
                      <b>{formatMoney(item.amount, "CNY")}</b>
                      <small>{expandedCats.has(item.name) ? "收起" : "展开"}</small>
                    </button>
                    {expandedCats.has(item.name) && (
                      <div className="expense-cat-node__items">
                        {(categoryDetails.get(item.name) ?? [])
                          .slice()
                          .sort((a, b) => b.record_date.localeCompare(a.record_date))
                          .map((detail) => (
                            <div className="expense-cat-node__item" key={detail.id}>
                              <span>{detail.record_date.slice(5)}</span>
                              <em>{detail.remark || "—"}</em>
                              <b>{formatMoney(detail.amount_minor, "CNY")}</b>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="expense-day-list">
                <div className="expense-cat-tree__head">最近记录</div>
                {expenseDays.map((day) => (
                  <div className="expense-day" key={day.date}>
                    <div className="expense-day__head">
                      <span>{formatDate(day.date, { month: "long", day: "numeric" })}</span>
                      <b>{formatMoney(day.total, "CNY")}</b>
                    </div>
                    <div className="expense-day__items">
                      {day.items.map((item) => (
                        <div className="expense-row" key={item.id}>
                          <span className="expense-row__cate">{item.category}</span>
                          <span className="expense-row__remark">{item.remark || "—"}</span>
                          <b>{formatMoney(item.amount_minor, "CNY")}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>

      {assetEditor && <AssetEditor asset={assetEditor === "new" ? null : assetEditor} onClose={() => setAssetEditor(null)} />}
      {planEditor && <FinancePlanEditor plan={planEditor === "new" ? null : planEditor} onClose={() => setPlanEditor(null)} />}
    </div>
  );
}
